import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, Copy, ExternalLink, Link2, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminTalentBookingLinkCard
 *
 * Admin-only tile rendered on `/admin/talent/:userId`. Shows the talent's
 * current `/book/<slug>`, copy/preview affordances, and an Override button
 * that opens a dialog with the same live availability check the talent
 * sees (`GET /talent/booking-link/check`) — but on save calls the admin
 * endpoint `PUT /admin/booking-links/{userId}` so ops can fix offensive
 * handles, merge-conflict duplicates, or grant high-value partners a
 * vanity slug without emailing support.
 *
 * Also lists the last 10 retired slugs so support can answer "where did
 * my old link go?" at a glance.
 */
const AdminTalentBookingLinkCard = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [candidate, setCandidate] = useState('');
  const [checkState, setCheckState] = useState({ checking: false, available: null, reason: null, normalized: null });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  // Alias preview modal — shows what a visitor to an old `/book/<alias>`
  // URL sees today (auto-redirect vs. clean fallback). Makes the chip list
  // actionable for support instead of read-only decoration.
  const [aliasPreview, setAliasPreview] = useState(null); // { slug, status, redirect_to }
  const [aliasLoading, setAliasLoading] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/booking-links/${userId}`);
      setData(r.data);
    } catch (e) {
      // Silent fail — shows a friendly empty-state card below.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const runAvailability = useCallback(async (value) => {
    if (!value || value.trim().length < 3) {
      setCheckState({ checking: false, available: null, reason: 'Too short — use at least 3 characters.', normalized: null });
      return;
    }
    setCheckState((s) => ({ ...s, checking: true }));
    try {
      const r = await api.get('/talent/booking-link/check', { params: { slug: value } });
      setCheckState({
        checking: false,
        available: r.data.available,
        reason: r.data.reason,
        normalized: r.data.slug,
      });
    } catch (e) {
      setCheckState({
        checking: false,
        available: false,
        reason: e?.response?.data?.detail || 'Invalid username',
        normalized: null,
      });
    }
  }, []);

  const onCandidateChange = (value) => {
    setCandidate(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAvailability(value), 350);
  };

  const openEdit = () => {
    setCandidate(data?.slug || '');
    setCheckState({ checking: false, available: null, reason: null, normalized: null });
    setEditOpen(true);
  };

  const save = async () => {
    if (!checkState.available) {
      toast.error(checkState.reason || 'Pick an available username first');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/admin/booking-links/${userId}`, { slug: candidate });
      toast.success(r.data.changed ? `Slug updated → /book/${r.data.slug}` : 'No change');
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save slug');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!data?.slug) return;
    try {
      // Copy the `/api/l/<slug>` resolver URL — it serves a personalised OG
      // preview to social crawlers, then redirects real humans to the clean
      // `/book/<slug>` page. Guarantees pastes render a real preview card.
      await navigator.clipboard.writeText(`${origin}/api/l/${data.slug}`);
      toast.success('Preview-ready link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const previewAlias = async (aliasSlug) => {
    setAliasPreview({ slug: aliasSlug, status: 'loading', redirect_to: null });
    setAliasLoading(true);
    try {
      // Hit the PUBLIC endpoint directly so we see exactly what a real
      // visitor would see — including the 301 redirect payload.
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/booking/public/${aliasSlug}`);
      const body = await res.json().catch(() => ({}));
      if (res.status === 200) {
        setAliasPreview({ slug: aliasSlug, status: 'live', redirect_to: null });
      } else if (res.status === 301 && body?.detail?.redirect_slug) {
        setAliasPreview({ slug: aliasSlug, status: 'redirects', redirect_to: body.detail.redirect_slug });
      } else {
        setAliasPreview({ slug: aliasSlug, status: 'missing', redirect_to: null });
      }
    } catch {
      setAliasPreview({ slug: aliasSlug, status: 'error', redirect_to: null });
    } finally {
      setAliasLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null; // Non-talent users / missing profile — hide silently.
  }

  const url = data.slug ? `${origin}/book/${data.slug}` : null;

  return (
    <Card data-testid="admin-booking-link-card">
      <CardHeader className="py-3 px-5">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Link2 className="w-4 h-4 text-cyan-600" />
          Booking Link
          {!data.is_active && (
            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
              inactive
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5 space-y-3">
        {url ? (
          <div className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2 text-sm" data-testid="admin-booking-link-url">
            <code className="flex-1 truncate">{url}</code>
            <Button size="sm" variant="ghost" onClick={copyLink} className="px-2" data-testid="admin-booking-link-copy-btn">
              <Copy className="w-3 h-3" />
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-700 hover:text-cyan-800 px-1"
              data-testid="admin-booking-link-preview-btn"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        ) : (
          <div className="text-xs text-gray-500">No slug assigned yet.</div>
        )}

        <Button size="sm" variant="outline" onClick={openEdit} data-testid="admin-override-slug-btn">
          <Pencil className="w-3 h-3 mr-1" />
          Override username
        </Button>

        {data.aliases?.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
              Retired slugs ({data.aliases.length})
            </div>
            <div className="flex flex-wrap gap-1" data-testid="admin-alias-list">
              {data.aliases.slice(0, 10).map((a) => (
                <button
                  type="button"
                  key={a.slug}
                  onClick={() => previewAlias(a.slug)}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-mono text-gray-600 hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-800 transition-colors"
                  title={`Retired ${a.retired_at ? new Date(a.retired_at).toLocaleString() : '?'} · ${a.change_source} — click to preview`}
                  data-testid={`admin-alias-chip-${a.slug}`}
                >
                  {a.slug}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Tap a chip to test where it routes today.
            </p>
          </div>
        )}
      </CardContent>

      {/* Alias preview modal — shows the real public-endpoint behaviour so
          support can confirm old links still route before a rename. */}
      <Dialog open={!!aliasPreview} onOpenChange={(v) => !v && setAliasPreview(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="alias-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Retired link preview
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 break-all">
              {origin}/book/{aliasPreview?.slug}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {aliasLoading || aliasPreview?.status === 'loading' ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking…
              </div>
            ) : aliasPreview?.status === 'redirects' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2" data-testid="alias-status-redirects">
                <div className="flex items-center gap-2 font-medium text-emerald-800">
                  <Check className="w-4 h-4" /> 301 Redirect
                </div>
                <p className="text-xs text-gray-700">
                  Visitors are auto-bounced to the current link:
                </p>
                <div className="bg-white border border-gray-200 rounded p-2 font-mono text-xs break-all">
                  {origin}/book/{aliasPreview.redirect_to}
                </div>
                <a
                  href={`${origin}/book/${aliasPreview.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-800"
                  data-testid="alias-open-tab-btn"
                >
                  <ExternalLink className="w-3 h-3" /> Open in new tab
                </a>
              </div>
            ) : aliasPreview?.status === 'live' ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-3" data-testid="alias-status-live">
                <div className="flex items-center gap-2 font-medium text-amber-800">
                  <X className="w-4 h-4" /> Unexpectedly live
                </div>
                <p className="text-xs text-gray-700 mt-1">
                  This slug resolves to a profile directly — it probably shouldn't.
                  Escalate so support can reassign it.
                </p>
              </div>
            ) : aliasPreview?.status === 'missing' ? (
              <div className="bg-red-50 border border-red-200 rounded p-3" data-testid="alias-status-missing">
                <div className="flex items-center gap-2 font-medium text-red-800">
                  <X className="w-4 h-4" /> Dead link
                </div>
                <p className="text-xs text-gray-700 mt-1">
                  Visitors get a 404 fallback. Old collateral won't route —
                  you may want to re-add this slug as an alias.
                </p>
              </div>
            ) : (
              <div className="text-gray-600 text-sm">Network error — try again.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAliasPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="admin-override-slug-dialog">
          <DialogHeader>
            <DialogTitle>Override booking username</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Admin override — use for offensive handles, partner vanity URLs, or
              merge-conflict duplicates. The old slug is retired and auto-redirects.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="admin-slug-input" className="text-xs">New URL</Label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500 shrink-0">{origin}/book/</span>
              <Input
                id="admin-slug-input"
                value={candidate}
                onChange={(e) => onCandidateChange(e.target.value)}
                placeholder="new-slug"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck="false"
                data-testid="admin-slug-input"
              />
            </div>
            <div className="min-h-[20px] text-xs flex items-center gap-1">
              {checkState.checking && (
                <span className="text-gray-500 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> checking…
                </span>
              )}
              {!checkState.checking && checkState.available === true && (
                <span className="text-emerald-700 inline-flex items-center gap-1" data-testid="admin-slug-available">
                  <Check className="w-3 h-3" /> Available — <code className="bg-emerald-50 px-1 rounded">/book/{checkState.normalized}</code>
                </span>
              )}
              {!checkState.checking && checkState.available === false && (
                <span className="text-red-700 inline-flex items-center gap-1" data-testid="admin-slug-unavailable">
                  <X className="w-3 h-3" /> {checkState.reason || 'Not available'}
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !checkState.available}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="admin-save-slug-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminTalentBookingLinkCard;
